// The Court Police desk by the ground-floor entrance, where wrong turns and errors end up (spec 2, rows 1, 2, 15 and
// 26). The notice is the officer on duty speaking: a speech bubble from the officer at the desk (art: police.officer in
// frontend/art/r_ground_work.py), and the underlying error, when there is one, typed on a slip lying on the desk
// (police.slip). Both are glued to the art with the camera variables WorldStage sets on :root.
import type { CSSProperties, ReactNode } from 'react';
import { useWorldScene } from '../../world/WorldStage';
import { useWorldMeta } from '../../world/assets';
import '../../world/fx_ground';
import { useMatchMedia } from '../../hooks/useMatchMedia';
import { MQ_DOCKET_STACK } from '../../lib/breakpoints';
import { anchorBox, lean, roomSpan, useViewport } from '../screens/clerkOffice';
import './police.css';

export default function PoliceNotice({ kicker, title, children, actions, detail, testId, headingLevel = 1 }: {
  kicker?: string;
  title: string;
  children?: ReactNode;
  actions?: ReactNode;
  detail?: string | null;
  testId?: string;
  headingLevel?: 1 | 2;
}) {
  // Above a route's own scene: an error takes the camera to the desk wherever it happened. The officer sits a little
  // below the middle of the screen, so what she says has the room above her.
  useWorldMeta();
  const vp = useViewport();
  const narrow = useMatchMedia(MQ_DOCKET_STACK);
  const officer = anchorBox('police.officer');
  const slip = anchorBox('police.slip');
  const shot = lean(officer, narrow ? [130, 1] : [200, 125], vp, { topFrac: narrow ? 0.62 : 0.58, within: roomSpan('police', [1360, 1534]) });
  // The officer at the desk is the one speaking the notice; her colleague at the door keeps quiet while she does.
  useWorldScene({ room: 'police', focus: shot.focus, center: shot.center,
    castOverride: { 'police.desk': { talking: true }, 'police.door': { talking: false } } }, 2);
  const vars = {
    '--ox': String(officer[0]), '--oy': String(officer[1]), '--slx': String(slip[0]), '--sly': String(slip[1]),
    '--fcx': String(shot.center[0]), '--fcy': String(shot.center[1]), '--fwp': `${shot.wp}px`,
  } as CSSProperties;
  const H = headingLevel === 1 ? 'h1' : 'h2';
  return (
    <div className="police" data-testid={testId} style={vars}>
      <section className="police-bubble police-card" aria-labelledby="police-title" data-scroll="intended">
        <p className="ui police-speaker">Officer on duty</p>
        {kicker && <p className="ui police-kicker">{kicker}</p>}
        <H id="police-title" className="hd police-title">{title}</H>
        {children && <div className="doc police-body">{children}</div>}
        {actions && <div className="px-actions police-actions">{actions}</div>}
      </section>
      {/* the bubble's tail, down to the officer's head */}
      <svg className="police-tail" viewBox="0 0 7 5" shapeRendering="crispEdges" aria-hidden="true" focusable="false">
        <path fill="#160f1a" d="M0 0H1V1H0ZM6 0H7V1H6ZM1 1H2V2H1ZM5 1H6V2H5ZM2 2H3V4H2ZM4 2H5V3H4ZM3 3H4V4H3ZM2 4H3V5H2Z" />
        <path fill="#fffbf2" d="M1 0H6V1H1ZM2 1H5V2H2ZM3 2H4V3H3Z" />
      </svg>
      {detail ? <p className="ui police-detail">{detail}</p> : null}
    </div>
  );
}
